<script lang="ts">
  import type { CompareSeries } from '../../api/compare'
  import { LEAD_WINDOWS, LEAD_WINDOW_IDS, type LeadWindow } from '../../map/layers/modelLayer'
  import { colorFor } from '../../chart/palette'
  import { layers } from '../../state/layers.svelte'
  import { layout } from '../../state/layout.svelte'
  import { location } from '../../state/location.svelte'
  import { models, setLeadWindow } from '../../state/models.svelte'
  import {
    compare,
    ensureCompare,
    isHidden,
    loadCompareFields,
    setCompareField,
    setCompareView,
    toggleSeries,
  } from '../../state/compare.svelte'
  import { MINUTE, HOUR } from '../../time'
  import Chart from '../Chart.svelte'
  import Panel from '../Panel.svelte'
  import CompareTable from './CompareTable.svelte'

  /**
   * The meteogram: one point, every model, the official forecast and what the
   * station measured, on one axis.
   *
   * It refetches on the point changing and on `models.revision` — "a run
   * landed" — and **not** on `models.polled`, which means "a cycle boundary
   * moved" and changes nothing that is drawn. Phase 4 built that distinction
   * for exactly this class of consumer.
   */

  let point = $derived(location.selected)

  /**
   * Whether anybody can see the chart. `/api/compare` is the most expensive
   * route this app has (every model's Zarr, plus NWS's gridpoint document and
   * the station's observations), and a point is selected on every launch, so
   * a collapsed panel asks for nothing. Opening it asks for the point shown.
   */
  let open = $state(false)

  $effect(() => {
    if (open) void loadCompareFields()
  })

  $effect(() => {
    // The three things that change the answer, read explicitly so the effect's
    // dependencies are visible rather than incidental: where, what, and
    // whether a run landed. `models.revision`, never `models.polled` — see the
    // note above. `ensureCompare` dedupes on exactly this triple, so flipping
    // to the table asks for nothing.
    const here = point
    const field = compare.field
    const revision = models.revision
    if (open && here) void ensureCompare(here.lat, here.lon, revision, field)
  })

  let body = $derived(compare.body)
  let series = $derived<CompareSeries[]>(body?.series ?? [])

  /**
   * The window, from the server's own clock rather than the browser's.
   *
   * `generated` is when the answer was made, and using it means the "now" the
   * chart is centred on is the same "now" the run ages were measured against.
   */
  let now = $derived(body ? new Date(body.generated) : new Date())

  /**
   * How far back to show, given how far ahead we are looking.
   *
   * A twelve-hour observed trace against a +3 h window would be four fifths of
   * the chart in the past, which is a chart of what already happened. A third
   * of the window, floored at an hour so the observations are always visible
   * and capped at six so a sixteen-day GFS does not drag a week of nothing
   * along with it.
   */
  let pastMs = $derived.by(() => {
    const ahead = LEAD_WINDOWS[models.window]
    if (!Number.isFinite(ahead)) return 6 * HOUR
    return Math.min(6 * HOUR, Math.max(1 * HOUR, (ahead * MINUTE) / 3))
  })

  let from = $derived(new Date(now.getTime() - pastMs))

  let to = $derived.by(() => {
    const ahead = LEAD_WINDOWS[models.window]
    if (Number.isFinite(ahead)) return new Date(now.getTime() + ahead * MINUTE)
    // `all` is bounded by the data rather than by a number: with the long-lead
    // profile that is sixteen days of GFS, which is exactly where the window
    // control earns itself.
    const last = Math.max(
      now.getTime(),
      ...series.flatMap((s) => s.points.map((p) => Date.parse(p.t))).filter((t) => !Number.isNaN(t)),
    )
    return new Date(last)
  })

  /**
   * The instant the map is drawing, if an animated layer is active.
   *
   * Clamped exactly the way `TimeSlider` clamps it, so the two cannot read
   * different frames: the index outlives the array across a layer change, and
   * a chart cursor an hour from the plate is the failure this panel exists to
   * make visible rather than to commit.
   */
  let cursor = $derived(
    layers.times.length > 0
      ? (layers.times[Math.min(layers.index, layers.times.length - 1)] ?? null)
      : null,
  )

  const WINDOW_LABELS: Record<LeadWindow, string> = {
    '3h': '+3 h',
    '6h': '+6 h',
    '12h': '+12 h',
    '48h': '+48 h',
    all: 'All',
  }

  function ageLabel(minutes: number | undefined): string {
    if (minutes === undefined) return ''
    if (minutes < 60) return `${minutes} m ago`
    const hours = Math.round(minutes / 60)
    return `${hours} h ago`
  }

  /** `HRRR · 21Z · 42 m ago`, or `NWS forecast · updated 18:46`. */
  function footerFor(s: CompareSeries): string {
    if (s.kind === 'model') {
      const cycle = s.run ? `${s.run.slice(9, 11)}Z` : ''
      const age = ageLabel(s.run_age_minutes)
      const partial = s.complete === false ? ' · partial' : ''
      return [cycle, age].filter(Boolean).join(' · ') + partial
    }
    if (s.kind === 'observed') {
      // The cadence rather than the count: 145 observations beside a dozen
      // dots reads as a bug, and the dots are thinned on purpose — a station
      // reporting every five minutes is 145 marks across 130 px.
      const step = observedCadence(s)
      return step ? `every ${step} min` : `${s.points.length} observations`
    }
    return s.window_minutes > 0 ? `${s.window_minutes / 60} h totals` : 'official'
  }

  /** The station's own reporting interval, as the median gap between reports. */
  function observedCadence(s: CompareSeries): number | null {
    const stamps = s.points.map((p) => Date.parse(p.t)).filter((t) => !Number.isNaN(t))
    if (stamps.length < 3) return null
    const gaps: number[] = []
    for (let i = 1; i < stamps.length; i++) gaps.push(stamps[i] - stamps[i - 1])
    gaps.sort((a, b) => a - b)
    return Math.max(1, Math.round(gaps[Math.floor(gaps.length / 2)] / MINUTE))
  }

  let station = $derived(body?.point.station ?? null)
  let hasAnySeries = $derived(series.length > 0)
</script>

<Panel id="meteogram" title="Meteogram" bind:open>
  {#if !point}
    <p class="muted">Tap the map or search to compare the models at a point.</p>
  {:else}
    <div class="body">
      <!-- Every field the catalog reports for at least one model. No second
           table: a field this deployment does not ingest has no chip. -->
      {#if compare.fields.length > 0}
        <div class="wx-tabs" role="tablist" aria-label="Chart field">
          {#each compare.fields as field (field.id)}
            <button
              type="button"
              role="tab"
              class="touch-target"
              class:on={field.id === compare.field}
              aria-selected={field.id === compare.field}
              onclick={() => setCompareField(field.id)}
            >
              {field.label}
            </button>
          {/each}
        </div>
      {/if}

      <!-- The same control the model picker has, because it already means
           exactly "how far ahead am I looking". One control, one preference. -->
      <div class="setting" role="group" aria-label="Look ahead">
        {#if layout.isDesktop}<span class="setting-label" aria-hidden="true">Look ahead</span>{/if}
        <span class="wx-seg">
          {#each LEAD_WINDOW_IDS as id (id)}
            <button
              type="button"
              class="touch-target"
              class:on={models.window === id}
              aria-pressed={models.window === id}
              onclick={() => setLeadWindow(id)}
            >
              {WINDOW_LABELS[id]}
            </button>
          {/each}
        </span>
      </div>

      {#if compare.status === 'loading' && !body}
        <p class="muted loading">Reading the models…</p>
      {/if}

      {#if body && hasAnySeries}
        <div class="view">
          <div class="wx-sec-head view-head">
            <span>{body.field.label}, {body.field.units}</span>
            <!-- The chart and its accessible form, one switch apart. -->
            <span class="wx-seg switch" role="group" aria-label="Show as">
              <button
                type="button"
                class="touch-target"
                class:on={compare.view === 'chart'}
                aria-pressed={compare.view === 'chart'}
                onclick={() => setCompareView('chart')}>Chart</button
              >
              <button
                type="button"
                class="touch-target"
                class:on={compare.view === 'table'}
                aria-pressed={compare.view === 'table'}
                onclick={() => setCompareView('table')}>Table</button
              >
            </span>
          </div>

          {#if compare.view === 'chart'}
            <Chart
              {series}
              field={body.field}
              {from}
              {to}
              {now}
              timezone={body.point.timezone}
              hidden={new Set(compare.hidden)}
              {cursor}
              height={layout.isDesktop ? 224 : 200}
            />
          {:else}
            <CompareTable {body} hidden={new Set(compare.hidden)} />
          {/if}
        </div>

        <!-- The legend *is* the control (Phase 11's square toggles: a swatch
             filled while the series is drawn, hollow while it is hidden), and
             the caption under it is the footer: which run each line came
             from. Comparing a five-hour-old GFS against a forty-minute-old
             HRRR is most of what this is for. -->
        <ul class="legend" aria-label="Series. Tap one to hide it">
          {#each series as s (s.id)}
            {@const off = isHidden(s.id)}
            <li>
              <button
                type="button"
                class="toggle touch-target"
                aria-pressed={!off}
                title={s.label}
                class:off
                onclick={() => toggleSeries(s.id)}
              >
                <span class="key" style:--sw={colorFor(s.kind, s.id)}></span>
                <span class="name">{s.label}</span>
              </button>
            </li>
          {/each}
        </ul>
        <p class="runs">
          {series
            .filter((s) => !isHidden(s.id))
            .map((s) => `${s.label} ${footerFor(s)}`)
            .join(' · ')}
        </p>
      {:else if body}
        <p class="muted">Nothing to draw for {body.field.label} here.</p>
      {/if}

      {#if body && body.unavailable.length > 0}
        <!-- Named with a reason, never silently absent: a missing line and a
             model nobody ingested look identical otherwise. -->
        <ul class="unavailable">
          {#each body.unavailable as entry (entry.id)}
            <li><span class="who">{entry.id}</span> · {entry.reason}</li>
          {/each}
        </ul>
      {/if}

      {#if station}
        <p class="station">
          Observed at {station.id}{station.name ? ` (${station.name})` : ''}{station.distance_mi !=
          null
            ? `, ${station.distance_mi} mi away`
            : ''}
        </p>
      {/if}

      {#if compare.error}
        <p class="error">{compare.error}</p>
      {/if}
    </div>
  {/if}
</Panel>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .body p {
    margin: 0;
  }

  .setting {
    display: grid;
    grid-template-columns: 76px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
  }

  .setting:has(> .wx-seg:only-child) {
    grid-template-columns: minmax(0, 1fr);
  }

  .setting-label {
    font-size: 12px;
    color: var(--color-muted);
  }

  .view {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 4px;
  }

  .view-head {
    align-items: center;
  }

  .switch {
    flex: none;
    letter-spacing: 0;
    text-transform: none;
    --seg-h: var(--switch-h, 24px);
  }

  .switch > button {
    padding: 0 10px;
  }

  /* The redesign's model chips: a 3 x 2 grid of square toggles, ink-bordered
     while the series is drawn, a hairline and muted text while it is hidden. */
  .legend {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
  }

  .toggle {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    height: var(--toggle-h, 36px);
    padding: 0 10px;
    border: 2px solid var(--color-text);
    background: none;
    font-size: var(--legend-fs, 14px);
    font-weight: 800;
    text-align: left;
    cursor: pointer;
  }

  .toggle.off {
    padding: 0 11px;
    border: 1px solid var(--color-rule);
    color: var(--color-muted);
  }

  .key {
    flex: none;
    width: 12px;
    height: 12px;
    border: 2px solid var(--sw);
    background: var(--sw);
  }

  .toggle.off .key {
    background: none;
  }

  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .runs {
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-muted);
  }

  .unavailable {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 12px;
    line-height: 1.5;
    color: var(--color-muted);
  }

  .who {
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .station,
  .loading {
    font-size: 12px;
    color: var(--color-muted);
  }
</style>
